import * as Sentry from '@sentry/browser';
import type { Event, Exception, StackFrame } from '@sentry/browser';
import type { WidgetBuildInfo } from './analytics/types';
import type { WidgetInitialConfig } from './widgetConfig';

const DEFAULT_SENTRY_DSN =
  'https://155b20631db017757e275dbeb71097f0@o9891.ingest.us.sentry.io/4511133317595136';

const EXTENSION_PROTOCOL_PREFIXES = [
  'chrome-extension://',
  'moz-extension://',
  'safari-web-extension://',
];

interface SentryInitOptions {
  enabledOverride?: boolean;
  environmentOverride?: string;
  releaseOverride?: string;
}

let isInitialized = false;

function isKnownBuildValue(value: string | undefined): value is string {
  return Boolean(value && value !== 'unknown');
}

function getConfiguredDsn(): string {
  return import.meta.env.VITE_SENTRY_DSN || DEFAULT_SENTRY_DSN;
}

function getConfiguredEnvironment(options?: SentryInitOptions): string {
  if (isKnownBuildValue(options?.environmentOverride)) {
    return options.environmentOverride;
  }

  return (
    import.meta.env.VITE_SENTRY_ENVIRONMENT ||
    (import.meta.env.PROD ? 'production' : 'development')
  );
}

function getConfiguredRelease(
  buildInfo: WidgetBuildInfo,
  options?: SentryInitOptions,
): string | undefined {
  if (isKnownBuildValue(options?.releaseOverride)) {
    return options.releaseOverride;
  }

  if (isKnownBuildValue(import.meta.env.VITE_SENTRY_RELEASE)) {
    return import.meta.env.VITE_SENTRY_RELEASE;
  }

  if (isKnownBuildValue(buildInfo.version)) {
    return buildInfo.version;
  }

  if (isKnownBuildValue(buildInfo.commitSha)) {
    return buildInfo.commitSha;
  }

  return undefined;
}

function getEventFrames(event: Event): StackFrame[] {
  return (event.exception?.values || []).flatMap(
    (exception: Exception) => exception.stacktrace?.frames || [],
  );
}

function isBrowserExtensionNoise(event: Event): boolean {
  const filenames = getEventFrames(event)
    .map((frame) => frame.filename)
    .filter((filename): filename is string => Boolean(filename));

  return (
    filenames.length > 0 &&
    filenames.every((filename) =>
      EXTENSION_PROTOCOL_PREFIXES.some((prefix) => filename.startsWith(prefix)),
    )
  );
}

// allowUrls only filters events that have stack frames — unhandled promise
// rejections from third-party scripts often arrive with no frames and bypass
// it. Drop any event whose frames contain no reference to our own script URL.
function isUnrelatedExternalNoise(event: Event): boolean {
  const frames = getEventFrames(event);

  if (frames.length === 0) {
    return true;
  }

  const widgetUrl = import.meta.url.split('?')[0];

  return !frames.some((frame) => frame.filename?.startsWith(widgetUrl));
}

function getHostOrigin(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return window.location.origin;
}

export function initSentry(
  buildInfo: WidgetBuildInfo,
  options?: SentryInitOptions,
): void {
  if (isInitialized) {
    return;
  }

  const dsn = getConfiguredDsn();

  Sentry.init({
    dsn,
    enabled: options?.enabledOverride ?? import.meta.env.PROD,
    environment: getConfiguredEnvironment(options),
    release: getConfiguredRelease(buildInfo, options),
    sendDefaultPii: false,
    maxBreadcrumbs: 20,
    // Only capture errors originating from this widget script, not from the
    // parent page or other third-party scripts on the integrator's site.
    allowUrls: [import.meta.url.split('?')[0]],
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications.',
    ],
    beforeSend(event) {
      if (isBrowserExtensionNoise(event) || isUnrelatedExternalNoise(event)) {
        return null;
      }

      return event;
    },
    initialScope: {
      tags: {
        runtime: 'browser',
      },
    },
  });

  isInitialized = true;
}

export function captureSentryTestError(source: string): void {
  Sentry.withScope((scope) => {
    scope.setTag('test_trigger_source', source);

    Sentry.captureException(
      new Error(`Manual Sentry test trigger (${source})`),
    );
  });
}

export function setWidgetSentryContext(options: {
  tenant: string;
  buildInfo: WidgetBuildInfo;
  initialConfig: WidgetInitialConfig;
}): void {
  const { tenant, buildInfo, initialConfig } = options;

  Sentry.setTag('tenant', tenant);
  Sentry.setTag('widget_version', buildInfo.version || 'unknown');
  Sentry.setTag('widget_commit', buildInfo.commitSha || 'unknown');

  Sentry.setContext('widget', {
    hostOrigin: getHostOrigin(),
    initialTab: initialConfig.initialTab,
    hasInitialOrigin: Boolean(initialConfig.initialOrigin),
    hasInitialDestination: Boolean(initialConfig.initialDestination),
    hasDepartureStopId: Boolean(initialConfig.departureStopId),
  });
}

export function captureWidgetBoundaryError(options: {
  error: Error;
  componentStack: string | null;
  tenant: string;
  buildInfo: WidgetBuildInfo;
  initialConfig: WidgetInitialConfig;
}): void {
  const { error, componentStack, tenant, buildInfo, initialConfig } = options;

  Sentry.withScope((scope) => {
    scope.setTag('tenant', tenant);
    scope.setTag('widget_version', buildInfo.version || 'unknown');
    scope.setTag('widget_commit', buildInfo.commitSha || 'unknown');
    scope.setTag('runtime', 'browser');

    scope.setContext('widget', {
      hostOrigin: getHostOrigin(),
      initialTab: initialConfig.initialTab,
      hasInitialOrigin: Boolean(initialConfig.initialOrigin),
      hasInitialDestination: Boolean(initialConfig.initialDestination),
      hasDepartureStopId: Boolean(initialConfig.departureStopId),
    });

    if (componentStack) {
      scope.setContext('preact', {
        componentStack,
      });
    }

    Sentry.captureException(error);
  });
}
